import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

function useCountUp(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const startTime = performance.now();
          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * end));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return { count, ref };
}

function StatCard({
  number,
  suffix,
  label,
}: {
  number: number;
  suffix: string;
  label: string;
}) {
  const { count, ref } = useCountUp(number);
  return (
    <div ref={ref} className="text-center md:text-left">
      <div className="text-3xl md:text-4xl font-bold text-teal-800 font-display">
        {count.toLocaleString()}
        {suffix}
      </div>
      <div className="text-xs md:text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function AnimatedSection({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {children}
    </div>
  );
}

const programs = [
  {
    title: "Bantuan Makanan",
    desc: "Menyediakan bekalan makanan harian dan juadah berbuka puasa kepada keluarga yang memerlukan.",
    icon: "ri-restaurant-line",
    color: "bg-primary-50",
    iconColor: "text-primary-600",
  },
  {
    title: "Pendidikan",
    desc: "Menyediakan kelas tambahan, kelas bahasa, dan bantuan kelengkapan sekolah kepada kanak-kanak miskin.",
    icon: "ri-book-open-line",
    color: "bg-warm-50",
    iconColor: "text-warm-600",
  },
  {
    title: "Perlindungan",
    desc: "Memberi perlindungan dan sokongan kepada pelarian serta mangsa keganasan dan penyalahgunaan.",
    icon: "ri-shield-check-line",
    color: "bg-teal-50",
    iconColor: "text-teal-600",
  },
  {
    title: "Kesihatan",
    desc: "Menganjurkan kempen kesihatan, pemeriksaan percuma, dan bantuan ubat-ubatan.",
    icon: "ri-heart-pulse-line",
    color: "bg-red-50",
    iconColor: "text-red-500",
  },
];

const testimonials = [
  {
    text: "Mulia Insan telah membantu keluarga saya mendapatkan bekalan makanan semasa musim banjir. Terima kasih atas bantuan yang tidak ternilai ini.",
    name: "Puan Aminah",
    role: "Penerima Bantuan, Kelantan",
  },
  {
    text: "Anak saya dapat menamatkan sekolah dengan bantuan kelas tambahan dari Mulia Insan. Kami sangat berterima kasih.",
    name: "Encik Razak",
    role: "Penerima Bantuan Pendidikan, Kuala Lumpur",
  },
  {
    text: "Program latihan AI yang mereka anjurkan telah membuka peluang kerjaya baru untuk saya. Sungguh berfaedah!",
    name: "Muhammad Faiz",
    role: "Peserta Latihan AI, Selangor",
  },
];

export default function Home() {
  const [tIndex, setTIndex] = useState(0);

  return (
    <div className="min-h-screen">
      {/* ===== HERO ===== */}
      <section className="relative h-[520px] md:h-[680px] flex items-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20golden%20sunlight%20over%20a%20peaceful%20Malaysian%20village%20community%20gathering%20with%20children%20and%20families%20helping%20each%20other%2C%20soft%20earth%20tones%2C%20green%20and%20golden%20warm%20lighting%2C%20humanitarian%20aid%20atmosphere%2C%20cinematic%20photography%2C%20high%20quality%2C%20emotional%20warmth&width=1600&height=900&seq=hero1&orientation=landscape"
          alt="Komuniti bantu-membantu"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 via-teal-800/60 to-transparent" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6 animate-fade-in">
              <div className="flex -space-x-2">
                <img
                  src="https://readdy.ai/api/search-image?query=portrait%20of%20a%20young%20smiling%20Malaysian%20volunteer%20wearing%20a%20green%20t-shirt%2C%20warm%20soft%20lighting%2C%20professional%20headshot%2C%20clean%20simple%20background&width=80&height=80&seq=ava1&orientation=squarish"
                  alt="Sukarelawan"
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=portrait%20of%20a%20friendly%20middle-aged%20Malaysian%20woman%20volunteer%20smiling%2C%20warm%20soft%20lighting%2C%20professional%20headshot%2C%20clean%20simple%20background&width=80&height=80&seq=ava2&orientation=squarish"
                  alt="Sukarelawan"
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=portrait%20of%20a%20young%20Malaysian%20man%20volunteer%20with%20a%20warm%20smile%2C%20soft%20lighting%2C%20professional%20headshot%2C%20clean%20simple%20background&width=80&height=80&seq=ava3&orientation=squarish"
                  alt="Sukarelawan"
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
              </div>
              <span className="text-white/80 text-xs md:text-sm">
                Lebih 5,000 orang telah terbantu
              </span>
            </div>

            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 animate-slide-up">
              Menyuluh Harapan,
              <br />
              <span className="font-light">Menebar Kasih</span>
            </h1>

            <p className="text-white/80 text-sm md:text-base leading-relaxed max-w-lg mb-8 animate-slide-up" style={{ animationDelay: "0.2s" }}>
              PERTUBUHAN MULIA INSAN MALAYSIA berdedikasi menyediakan bantuan
              makanan, pendidikan, perlindungan, dan kesihatan kepada pelarian,
              mangsa bencana, serta golongan miskin & asnaf di seluruh Malaysia.
            </p>

            <div className="flex flex-wrap gap-3 animate-slide-up" style={{ animationDelay: "0.35s" }}>
              <Link
                to="/donate"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-teal-900 text-sm font-semibold rounded-full hover:bg-gray-50 transition-colors"
              >
                Sumbang Sekarang
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/40 text-white text-sm font-medium rounded-full hover:bg-white/10 transition-colors"
              >
                Ketahui Lebih Lanjut
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== IMPACT STATS ===== */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-3">
                Impak Kami
              </p>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Setiap Sumbangan
                <br />
                <span className="font-light text-gray-500">
                  Mengubah Kehidupan
                </span>
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                Sejak penubuhan kami, kami telah menyalurkan bantuan kepada
                ribuan keluarga di seluruh Malaysia. Dengan sokongan anda, kami
                mampu meneruskan usaha mulia ini demi masyarakat yang lebih baik.
              </p>
              <Link
                to="/programs"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 transition-colors"
              >
                Lihat Program Kami <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-6 md:gap-8">
              <StatCard
                number={5000}
                suffix="+"
                label="Orang Terbantu"
              />
              <StatCard
                number={120}
                suffix="+"
                label="Program Dilaksanakan"
              />
              <StatCard
                number={350}
                suffix="K"
                label="Ringgit Sumbangan"
              />
              <StatCard
                number={800}
                suffix="+"
                label="Sukarelawan Aktif"
              />
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ===== PROGRAMS PREVIEW ===== */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Program Kami
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900">
              Empat Tonggak Kebaikan
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {programs.map((p) => (
              <div
                key={p.title}
                className="group rounded-xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow bg-white"
              >
                <div className={`${p.color} h-40 md:h-44 flex items-center justify-center`}>
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
                    <i className={`${p.icon} ${p.iconColor} text-xl md:text-2xl`} />
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="font-semibold text-sm md:text-base text-gray-900 mb-1.5">
                    {p.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-10">
            <Link
              to="/programs"
              className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors"
            >
              Lihat Semua Program <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </AnimatedSection>
      </section>

      {/* ===== ACTIVITIES 2025 ===== */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-5xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Aktiviti Terkini
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900">
              Aktiviti Kami pada 2025
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto mt-3 leading-relaxed">
              Menyambut tahun 2025 dengan penuh semangat, kami terus mengukir
              kisah kebaikan bersama masyarakat yang memerlukan di seluruh Malaysia.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-gray-200 bg-white shadow-sm">
            <div className="relative w-full">
              <img
                src="https://storage.readdy-site.link/project_files/eba1094b-9d16-42e7-9800-2a6f91210332/f565fc68-c811-439d-9c7a-493a6ec2f3e7_155dc229b6934ba090ff85889c88e719.png?v=2d77814a52d152379f0f411d44727002"
                alt="Aktiviti PERTUBUHAN MULIA INSAN MALAYSIA pada 2025"
                className="w-full h-auto max-h-[600px] object-contain"
              />
            </div>
            <div className="p-5 md:p-8">
              <div className="flex items-center gap-2 mb-3">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-medium">
                  <i className="ri-calendar-line" />
                  2025
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-warm-50 text-warm-600 text-xs font-medium">
                  <i className="ri-heart-line" />
                  Kebaikan Terus Berlanjutan
                </div>
              </div>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                Gambar ini merakamkan salah satu sesi aktiviti PERTUBUHAN MULIA
                INSAN MALAYSIA sepanjang tahun 2025. Melalui kerjasama sukarelawan
                dan sumbangan masyarakat, kami terus menyampaikan bantuan kepada
                golongan memerlukan dengan penuh kasih dan amanah.
              </p>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ===== TRAINING PREVIEW ===== */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-100">
        <AnimatedSection className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20Malaysian%20adults%20sitting%20in%20a%20modern%20computer%20training%20classroom%20with%20laptops%2C%20warm%20earth%20tone%20lighting%2C%20green%20accents%20in%20decor%2C%20diverse%20ethnicities%2C%20friendly%20learning%20atmosphere%2C%20clean%20minimal%20interior%20design&width=700&height=500&seq=training1&orientation=landscape"
                alt="Sesi latihan komputer"
                className="w-full rounded-xl object-cover h-60 md:h-80"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
                Latihan AI & Komputer
              </p>
              <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Membina Kemahiran,
                <br />
                <span className="font-light text-gray-500">
                  Mencipta Masa Depan
                </span>
              </h2>
              <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
                Kami percaya teknologi dapat mengubah kehidupan. Program latihan
                AI dan komputer kami membantu golongan miskin, pelarian, dan
                asnaf memperoleh kemahiran digital yang relevan untuk peluang
                pekerjaan dan peningkatan ekonomi.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  { icon: "ri-robot-2-line", text: "Asas Kecerdasan Buatan (AI)" },
                  { icon: "ri-computer-line", text: "Kemahiran Asas Komputer & Internet" },
                  { icon: "ri-code-box-line", text: "Pengenalan kepada Pengaturcaraan" },
                  { icon: "ri-briefcase-line", text: "Digital Skills untuk Kerjaya" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-50">
                      <i className={`${item.icon} text-teal-600 text-sm`} />
                    </div>
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/training"
                className="inline-flex items-center gap-2 px-6 py-3 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors"
              >
                Ketahui Lebih Lanjut <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </section>

      {/* ===== DONATION CTA ===== */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <AnimatedSection className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900 mb-3">
            Sumbangan Anda, Harapan Mereka
          </h2>
          <p className="text-sm md:text-base text-gray-500 max-w-xl mx-auto mb-8 leading-relaxed">
            Setiap ringgit yang anda sumbangkan akan digunakan sepenuhnya untuk
            bantuan makanan, pendidikan, perlindungan, dan kesihatan. Tiada
            potongan pentadbiran.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 max-w-2xl mx-auto mb-10">
            {[
              { amount: "RM10", desc: "Bekalan makanan 1 hari" },
              { amount: "RM50", desc: "Kelas tambahan 1 bulan" },
              { amount: "RM100", desc: "Pakej bantuan lengkap" },
            ].map((d) => (
              <div
                key={d.amount}
                className="rounded-xl border border-gray-200 p-5 hover:border-teal-300 hover:shadow-sm transition-all cursor-pointer"
              >
                <div className="text-xl md:text-2xl font-bold text-teal-700 mb-1">
                  {d.amount}
                </div>
                <div className="text-xs text-gray-500">{d.desc}</div>
              </div>
            ))}
          </div>

          <Link
            to="/donate"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-teal-700 text-white text-sm font-semibold rounded-full hover:bg-teal-800 transition-colors"
          >
            Buat Sumbangan <i className="ri-arrow-right-line" />
          </Link>
        </AnimatedSection>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-14 md:py-20 px-4 md:px-8 lg:px-12 bg-earth-50">
        <AnimatedSection className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <p className="text-xs font-medium text-teal-600 uppercase tracking-wider mb-2">
              Testimoni
            </p>
            <h2 className="font-display text-2xl md:text-4xl font-bold text-gray-900">
              Apa Kata Mereka
            </h2>
          </div>

          <div className="relative bg-white rounded-2xl p-6 md:p-10 border border-gray-100">
            <div className="flex items-center gap-2 mb-5">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-900 text-white text-xs">
                <i className="ri-star-fill text-warm-400 text-xs" />
                4.9
              </div>
            </div>

            <p className="font-display text-base md:text-lg text-gray-700 leading-relaxed mb-8">
              &ldquo;{testimonials[tIndex].text}&rdquo;
            </p>

            <div className="flex items-center gap-3">
              <img
                src={`https://readdy.ai/api/search-image?query=portrait%20of%20a%20Malaysian%20person%20with%20a%20warm%20smile%20natural%20lighting%20simple%20background&width=60&height=60&seq=tava${tIndex}&orientation=squarish`}
                alt={testimonials[tIndex].name}
                className="w-11 h-11 rounded-full object-cover"
              />
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {testimonials[tIndex].name}
                </div>
                <div className="text-xs text-gray-500">
                  {testimonials[tIndex].role}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6 justify-end">
              <button
                onClick={() =>
                  setTIndex((i) => (i === 0 ? testimonials.length - 1 : i - 1))
                }
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                aria-label="Previous testimonial"
              >
                <i className="ri-arrow-left-line text-gray-600" />
              </button>
              <button
                onClick={() =>
                  setTIndex((i) => (i === testimonials.length - 1 ? 0 : i + 1))
                }
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-900 hover:bg-gray-800 transition-colors"
                aria-label="Next testimonial"
              >
                <i className="ri-arrow-right-line text-white" />
              </button>
            </div>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
}